import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

export const UserManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [user, setUser] = useState({
    email: "",
    password: "",
    password_confirmation: "",
    name: "",
    role: "user",
    document: "",
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axiosInstance.get(`/users/${id}`);
      const data = response.data;
      setUser({
        ...data,
        password: "",
        password_confirmation: "",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: t("errorFetchingUser"),
        severity: "error",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = id ? "PATCH" : "POST";
      const url = id
        ? `${process.env.REACT_APP_API_URL}/users/${id}`
        : `${process.env.REACT_APP_API_URL}/users`;

      const userData = {
        user: {
          email: user.email,
          name: user.name,
          role: user.role,
          document: user.document,
        },
      };

      if (user.password) {
        userData.user.password = user.password;
        userData.user.password_confirmation = user.password;
      }

      await axiosInstance({
        method: method,
        url: url,
        data: userData,
      });

      setNotification({
        open: true,
        message: id ? t("userUpdatedSuccessfully") : t("userCreatedSuccessfully"),
        severity: "success",
      });
      navigate("/user-list");
    } catch (error) {
      setNotification({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {id ? t("editUser") : t("createNewUser")}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("email")}
            margin="normal"
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
          />
          <TextField
            fullWidth
            label={t("name")}
            margin="normal"
            value={user.name}
            onChange={(e) => setUser({ ...user, name: e.target.value })}
          />
          <TextField
            fullWidth
            label={t("document")}
            margin="normal"
            value={user.document}
            onChange={(e) => setUser({ ...user, document: e.target.value })}
          />
          <TextField
            fullWidth
            type="password"
            label={t("password")}
            margin="normal"
            value={user.password}
            onChange={(e) =>
              setUser({
                ...user,
                password: e.target.value,
                password_confirmation: e.target.value,
              })
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("role")}</InputLabel>
            <Select
              value={user.role}
              label={t("role")}
              onChange={(e) => setUser({ ...user, role: e.target.value })}
            >
              <MenuItem value="user">{t("user")}</MenuItem>
              <MenuItem value="admin">{t("admin")}</MenuItem>
            </Select>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            {id ? t("updateUser") : t("createUser")}
          </Button>
        </form>
      </Paper>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
